import { FloatingFocusManager } from '@floating-ui/react';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import React, { Fragment, useEffect, useRef } from 'react';

const Content = (props) =>
  // When the popover is rendered in a portal
  // The Content should be wrapped in the same classes as the container
  props.withPortal ? (
    <div className={props.className}>{props.children}</div>
  ) : (
    // Otherwise just show the content as it is
    <Fragment>{props.children}</Fragment>
  );

const PopoverContentElement = ({
  context,
  initialFocus,
  withPortal,
  classNames,
  floating,
  contentOffset,
  floatingProps,
  maxHeight,
  x,
  y,
  hasClose,
  handleClose,
  children,
  arrowPosition,
  handleLastElementFocus,
  hideArrow,
  closeOnBackwardsFocus = false,
  onMount = () => {},
  focusOrder,
  lastElementTabIndex,
}) => {
  const contentElement = useRef(null);

  useEffect(() => {
    onMount(contentElement);
  }, []);

  const onBlurCapture = (e) => {
    // If we are moving with focus backwards (shift + tab) from this element,
    // then we need to close the popup
    const { target, currentTarget, relatedTarget } = e;
    if (target === currentTarget) {
      if (target.parentElement.previousSibling === relatedTarget) {
        handleClose();
      }
    }
  };

  return (
    <FloatingFocusManager
      context={context}
      order={focusOrder}
      modal={!closeOnBackwardsFocus}
      returnFocus
      initialFocus={initialFocus}
    >
      <Content withPortal={withPortal} className={classNames.popoverPortal}>
        <div
          onBlurCapture={closeOnBackwardsFocus ? onBlurCapture : null}
          ref={floating}
          className={classNames.popoverContent}
          style={{
            transform: `translate(${Math.round(x, 2)}px,${Math.round(y, 2)}px)`,
            margin: contentOffset,
            maxHeight,
          }}
          {...floatingProps}
          tabIndex={closeOnBackwardsFocus ? 0 : floatingProps.tabIndex}
        >
          <div role="presentation" ref={contentElement}>
            {hasClose && (
              <ButtonWithIcon
                isIcon
                isNaked
                icon="close"
                className={classNames.closeButton}
                aria-label="Close Popup"
                onClick={handleClose}
              />
            )}
            {children}
            <div
              role="button"
              tabIndex={lastElementTabIndex}
              aria-label=""
              aria-describedby=""
              onFocus={handleLastElementFocus}
              className="popover__last-element"
            />
          </div>
        </div>
        {!hideArrow && (
          <div
            className={classNames.arrow}
            style={{
              // prettier-ignore
              transform: `translate(
                ${Math.round(arrowPosition.x, 2)}px,
                ${Math.round(arrowPosition.y, 2)}px
              )`,
            }}
          />
        )}
      </Content>
    </FloatingFocusManager>
  );
};

export default PopoverContentElement;
